import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company: "",
    phone: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = isLogin ? "/client/login" : "/client/register";
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("client_token", response.data.token);
      localStorage.setItem("client_user", JSON.stringify(response.data.user));
      
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      navigate("/portal/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-16" data-testid="client-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src="https://customer-assets.emergentagent.com/job_1eeea6a6-945d-4052-ae21-011aad17a3a1/artifacts/n0vcoewk_fulllogo.png" 
              alt="Astute Builders" 
              className="h-24 mx-auto mb-4"
            />
          </Link>
          <h1 className="font-['Oswald'] text-3xl font-bold text-white">
            Client Portal
          </h1>
          <p className="text-[#A1A1AA] mt-2">
            {isLogin ? "Sign in to manage your invoices" : "Create your account"}
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#121212] border border-[#2A2A2A] p-8">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-white text-sm mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="form-input w-full"
                    placeholder="John Doe"
                    data-testid="name-input"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input w-full"
                    placeholder="Your Company Name"
                    data-testid="company-input"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input w-full"
                    placeholder="(123) 456-7890"
                    data-testid="phone-input"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="block text-white text-sm mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input w-full"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <label className="block text-white text-sm mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                className="form-input w-full"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Building2 size={18} />
                  {isLogin ? "Sign In" : "Create Account"}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#7ED4C6] hover:text-[#9BE0D5] text-sm"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>

        {/* Admin Link */}
        <div className="mt-6 text-center">
          <Link to="/admin" className="text-[#A1A1AA] hover:text-white text-sm">
            Admin Login →
          </Link>
        </div>
      </div>
    </div>
  );
}
